import { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Phone, Mail, MapPin, User, FileText, Calendar, AlertTriangle, AlertCircle, CheckCircle, Banknote, MessageCircle, Clock, CalendarPlus, Wrench, Package, Eye, ClipboardList } from 'lucide-react';
import clsx from 'clsx';
import { Card } from '../shared/components/Card.js';
import { WinkelLabel } from '../shared/components/WinkelLabel.js';
import { useAuth } from '../auth/AuthContext.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { formatDatum, formatValuta } from '../shared/utils/formatters.js';
import { useDossierCommunicatie } from '../../modules/communicatie/hooks/useCommunicatie.js';
import { useDossierAfspraken } from '../../modules/planning/hooks/usePlanningApi.js';
import { useDossierTickets, useDossierGarantie } from '../../modules/service/hooks/useService.js';

function parseNaam(naam: string): string {
  try { return JSON.parse(naam).nl; } catch { return naam; }
}

function formatLeverDatum(date: string | null): string {
  if (!date) return '—';
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date));
}

const apparaatStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
  VERWACHT: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Verwacht' },
  BESTELD: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Besteld' },
  ONDERWEG: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Onderweg' },
  BINNEN: { bg: 'bg-green-100', text: 'text-green-700', label: 'Binnen' },
  GELEVERD: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Geleverd' },
  DEFECT: { bg: 'bg-red-100', text: 'text-red-700', label: 'Defect' },
};

const vorderingStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
  INGEDIEND: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Ingediend' },
  TER_GOEDKEURING: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Ter goedkeuring' },
  GOEDGEKEURD: { bg: 'bg-green-100', text: 'text-green-700', label: 'Goedgekeurd' },
  BETWIST: { bg: 'bg-red-100', text: 'text-red-700', label: 'Betwist' },
};

const regelStatusConfig: Record<string, { bg: string; text: string }> = {
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-600' },
  GOEDGEKEURD: { bg: 'bg-green-100', text: 'text-green-700' },
  BETWIST: { bg: 'bg-red-100', text: 'text-red-700' },
};

// Sort apparaten: DEFECT first, then VERWACHT/BESTELD, then rest
const apparaatSortOrder: Record<string, number> = {
  DEFECT: 0, VERWACHT: 1, BESTELD: 2, ONDERWEG: 3, BINNEN: 4, GELEVERD: 5,
};

export function WinkelDossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { accessibleEntities, activeEntity } = useAuth();
  const { data: dossier, isLoading } = useQuery({
    queryKey: ['dossier-volledig', id],
    queryFn: () => apiFetch<any>(`/api/buitendienst/dossiers/${id}/volledig`),
    enabled: !!id,
  });
  const { data: commData } = useDossierCommunicatie(id || null);
  const { data: afsprakenData } = useDossierAfspraken(id);
  const { data: serviceTickets = [] } = useDossierTickets(id);
  const { data: garantieInfo = [] } = useDossierGarantie(id);
  const { data: leverbonnenData } = useQuery({
    queryKey: ['leverbonnen-dossier', id],
    queryFn: () => apiFetch<{ items: any[] }>(`/api/logistiek/leverbonnen?dossierId=${id}`),
    enabled: !!id,
  });

  if (isLoading) return (
    <div className="flex justify-center py-12">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!dossier) return <div className="text-center py-12 text-gray-500">Dossier niet gevonden</div>;

  const stappen = dossier.trajectstappen || [];
  const apparaten = [...(dossier.apparaten || [])].sort(
    (a: any, b: any) => (apparaatSortOrder[a.status] ?? 9) - (apparaatSortOrder[b.status] ?? 9)
  );
  const vorderingsstaten = dossier.vorderingsstaten || [];
  const contacten = dossier.dossierContactpersonen || [];
  const documenten = dossier.documenten || [];
  const adressen = dossier.adressen || [];

  const binnenCount = apparaten.filter((a: any) => a.status === 'BINNEN' || a.status === 'GELEVERD').length;
  const totaalCount = apparaten.length;

  // Alert logic — use dossier leveringsdatum (falls back to vorderingsstaat)
  const leverDatum = dossier.leveringsdatum || vorderingsstaten[0]?.leverDatum || null;
  const dagenTotLevering = leverDatum
    ? Math.ceil((new Date(leverDatum).getTime() - Date.now()) / 86400000)
    : null;
  const apparatenCompleet = totaalCount === 0 || binnenCount === totaalCount;
  const heeftBetwist = vorderingsstaten.some((v: any) => v.status === 'BETWIST');

  let alertType: 'URGENT' | 'AANDACHT' | null = null;
  if (dagenTotLevering !== null && dagenTotLevering < 21 && !apparatenCompleet) alertType = 'URGENT';
  else if ((dagenTotLevering !== null && dagenTotLevering < 21) || heeftBetwist) alertType = 'AANDACHT';

  // Belangrijke datums from trajectstappen
  const belangrijkeDatums = stappen
    .filter((s: any) => s.geplandeDatum || s.afgerondDatum)
    .map((s: any) => ({
      naam: parseNaam(s.template.naam),
      icon: s.template.icon,
      datum: s.afgerondDatum || s.geplandeDatum,
      status: s.status,
    }));

  const scrollToSection = (key: string) => {
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate('/winkel')} className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft size={16} /> Terug naar overzicht
      </button>

      {/* 3-panel layout */}
      <div className="flex gap-6">

        {/* === LEFT PANEL: Trajectstappen === */}
        <div className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-6">
            <Card>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Traject</h3>
              <div className="space-y-0">
                {stappen.map((s: any, i: number) => {
                  const isCompleted = s.status === 'completed';
                  const isActive = s.status === 'active';
                  const isPending = s.status === 'pending';
                  return (
                    <div key={s.id} className="flex gap-3">
                      {/* Timeline line + circle */}
                      <div className="flex flex-col items-center">
                        <div className={clsx(
                          'rounded-full flex items-center justify-center flex-shrink-0',
                          isCompleted && 'w-7 h-7 bg-green-500 text-white',
                          isActive && 'w-8 h-8 border-[3px] border-gold bg-gold/10',
                          isPending && 'w-7 h-7 bg-gray-100 text-gray-400',
                        )}>
                          {isCompleted ? <CheckCircle size={14} /> : <span className="text-sm">{s.template.icon}</span>}
                        </div>
                        {i < stappen.length - 1 && (
                          <div className={clsx(
                            'w-0.5 flex-1 min-h-[24px]',
                            isCompleted ? 'bg-green-400' : 'bg-gray-200 border-dashed border-l-2 border-gray-300 w-0',
                          )} />
                        )}
                      </div>
                      {/* Label + date */}
                      <button
                        onClick={() => scrollToSection(`stap-${s.id}`)}
                        className="text-left pb-4 hover:opacity-80"
                      >
                        <p className={clsx(
                          'text-sm font-medium leading-tight',
                          isActive ? 'text-gray-900' : 'text-gray-500',
                        )}>
                          {parseNaam(s.template.naam)}
                        </p>
                        {(s.afgerondDatum || s.geplandeDatum) && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {formatDatum(s.afgerondDatum || s.geplandeDatum)}
                          </p>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>

        {/* === CENTER PANEL: Hoofdcontent === */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Mobile timeline */}
          <div className="lg:hidden overflow-x-auto">
            <div className="flex items-center gap-1 pb-2 min-w-max">
              {stappen.map((s: any, i: number) => (
                <div key={s.id} className="flex items-center">
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0',
                    s.status === 'completed' && 'bg-green-500 text-white',
                    s.status === 'active' && 'ring-2 ring-gold bg-gold/10',
                    s.status === 'pending' && 'bg-gray-100 text-gray-400',
                  )}>
                    {s.status === 'completed' ? '✓' : s.template.icon}
                  </div>
                  {i < stappen.length - 1 && (
                    <div className={clsx('w-6 h-0.5', s.status === 'completed' ? 'bg-green-400' : 'bg-gray-200')} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sectie 1: Dossier Header */}
          <Card>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">{dossier.klantNaam}</h1>
                  <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{dossier.dossierNummer}</span>
                  {/* Wie meerdere winkels heeft ziet bij welke winkel dit dossier hoort */}
                  {dossier.entity?.name && (accessibleEntities.length > 1 || dossier.entityId !== activeEntity?.id) && <WinkelLabel naam={dossier.entity.name} className="text-xs" />}
                </div>
                <p className="text-sm text-gray-500 mt-1">{dossier.adres}, {dossier.postcode} {dossier.stad}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  {dossier.bestelbonNr && (
                    <span className="text-xs text-gray-500">Bestelbon <strong className="text-gray-700">{dossier.bestelbonNr}</strong></span>
                  )}
                  {dossier.verkoper && (
                    <span className="text-xs text-gray-500">Verkoper <strong className="text-gray-700">{dossier.verkoper}</strong></span>
                  )}
                  {dossier.plaatser && (
                    <span className="text-xs text-gray-500">Plaatser <strong className="text-gray-700">{dossier.plaatser}</strong></span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {alertType === 'URGENT' && (
                  <span className="flex items-center gap-1 text-xs font-bold bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
                    <AlertTriangle size={14} /> URGENT
                  </span>
                )}
                {alertType === 'AANDACHT' && (
                  <span className="flex items-center gap-1 text-xs font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">
                    <AlertCircle size={14} /> AANDACHT
                  </span>
                )}
                {!alertType && (
                  <span className="flex items-center gap-1 text-xs font-bold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                    <CheckCircle size={14} /> OP SCHEMA
                  </span>
                )}
              </div>
            </div>
            {dossier.plaatsingsdatumStart && (
              <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-600">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  Plaatsing: <strong>{formatLeverDatum(dossier.plaatsingsdatumStart)}{dossier.plaatsingsdatumEind ? ` — ${formatLeverDatum(dossier.plaatsingsdatumEind)}` : ''}</strong>
                </span>
              </div>
            )}
          </Card>

          {/* Sectie 2: Apparaten */}
          <div ref={el => { sectionRefs.current['apparaten'] = el; }}>
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Apparaten
                  {totaalCount > 0 && (
                    <span className={clsx(
                      'ml-2 text-sm font-medium',
                      binnenCount === totaalCount ? 'text-green-600' : 'text-amber-600',
                    )}>
                      ({binnenCount}/{totaalCount} binnen)
                    </span>
                  )}
                </h2>
                {totaalCount > 0 && (
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full', binnenCount === totaalCount ? 'bg-green-500' : 'bg-amber-500')}
                      style={{ width: `${(binnenCount / totaalCount) * 100}%` }}
                    />
                  </div>
                )}
              </div>
              {apparaten.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Geen apparaten gekoppeld</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2 pr-4">Apparaat</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2 pr-4">Merk</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2 pr-4">Status</th>
                        <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Binnen op</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apparaten.map((a: any) => {
                        const cfg = apparaatStatusConfig[a.status] || apparaatStatusConfig.VERWACHT;
                        return (
                          <tr key={a.id} className="border-b border-gray-50">
                            <td className="py-2.5 pr-4 text-sm font-medium text-gray-900">{a.naam}</td>
                            <td className="py-2.5 pr-4 text-sm text-gray-600">{a.merk || a.leverancier || '—'}</td>
                            <td className="py-2.5 pr-4">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                                {cfg.label}
                              </span>
                            </td>
                            <td className="py-2.5 text-sm text-gray-500">
                              {a.binnenDatum ? formatDatum(a.binnenDatum) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Sectie 3: Vorderingsstaten */}
          <div ref={el => { sectionRefs.current['vorderingsstaten'] = el; }}>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Vorderingsstaten</h2>
            {vorderingsstaten.length === 0 ? (
              <Card>
                <p className="text-sm text-gray-400 text-center py-4">Geen vorderingsstaten</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {vorderingsstaten.map((v: any) => {
                  const cfg = vorderingStatusConfig[v.status] || vorderingStatusConfig.INGEDIEND;
                  return (
                    <Card key={v.id}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-xl font-bold text-gray-900">{formatValuta(v.totaal)}</p>
                          <p className="text-xs text-gray-400">
                            Subtotaal {formatValuta(v.subtotaal)} + BTW {v.btwPercentage}% ({formatValuta(v.btwBedrag)})
                          </p>
                        </div>
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>
                          {cfg.label}
                        </span>
                      </div>
                      {v.opmerking && (
                        <p className="text-sm text-gray-600 italic mb-3">"{v.opmerking}"</p>
                      )}
                      {/* Regels */}
                      <div className="border-t border-gray-100 pt-3 space-y-1.5">
                        {(v.regels || []).map((r: any) => {
                          const rCfg = regelStatusConfig[r.status] || regelStatusConfig.PENDING;
                          return (
                            <div key={r.id} className="flex items-center justify-between text-sm">
                              <div className="flex-1 min-w-0">
                                <span className="text-gray-700">{r.categorie}</span>
                                <span className="text-gray-400 mx-1">—</span>
                                <span className="text-gray-500">{r.omschrijving}</span>
                                {r.toelichting && (
                                  <p className="text-xs text-gray-400 mt-0.5">{r.toelichting}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${rCfg.bg} ${rCfg.text}`}>
                                  {r.status === 'PENDING' ? '—' : r.status === 'GOEDGEKEURD' ? '✓' : '✗'}
                                </span>
                                <span className="font-medium text-gray-900 w-16 text-right">{formatValuta(r.bedrag)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {v.status === 'TER_GOEDKEURING' && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/vorderingsstaten/${v.id}/beoordelen`); }}
                            className="px-4 py-2 bg-gold text-white text-sm font-medium rounded-lg hover:bg-gold/90 transition-colors"
                          >
                            Beoordelen
                          </button>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sectie 4: Leverbonnen */}
          {leverbonnenData?.items && leverbonnenData.items.length > 0 && (
            <div ref={el => { sectionRefs.current['leverbonnen'] = el; }}>
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ClipboardList size={18} className="text-blue-600" />
                    Leverbonnen
                    <span className="text-sm font-medium text-gray-400">({leverbonnenData.items.length})</span>
                  </h2>
                  <button
                    onClick={() => navigate('/logistiek/leverbonnen')}
                    className="text-xs text-gold hover:underline"
                  >
                    Alle leverbonnen →
                  </button>
                </div>
                <div className="space-y-3">
                  {leverbonnenData.items.map((lb: any) => (
                    <div key={lb.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', {
                              'bg-blue-100 text-blue-700': lb.type === 'ELECTROLUX',
                              'bg-purple-100 text-purple-700': lb.type === 'BEKO',
                              'bg-red-100 text-red-700': lb.type === 'NOBILIA',
                              'bg-gray-100 text-gray-600': lb.type === 'OVERIG',
                            })}>
                              {lb.type}
                            </span>
                            <span className="text-sm font-medium text-gray-700">{lb.leverancier}</span>
                            {lb.referentie && <span className="text-xs text-gray-400 font-mono">{lb.referentie}</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-400">{lb.items?.length || 0} items</span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs text-gray-400">{formatDatum(lb.ontvangstDatum || lb.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {lb.imageUrl && (
                          <button
                            onClick={() => window.open(lb.imageUrl, '_blank')}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <Eye size={13} />
                            Scan
                          </button>
                        )}
                        <span className={clsx('px-2 py-0.5 rounded text-xs font-bold text-white', {
                          'bg-blue-500': lb.status === 'NIEUW',
                          'bg-green-500': lb.status === 'VERWERKT',
                          'bg-red-500': lb.status === 'PROBLEEM',
                        })}>
                          {lb.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* === RIGHT PANEL: Info & Acties === */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <div className="sticky top-6 space-y-4">

            {/* Klantgegevens */}
            <Card>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Klantgegevens</h3>
              <div className="space-y-2.5">
                <p className="text-sm font-medium text-gray-900">{dossier.klantNaam}</p>
                {dossier.telefoon && (
                  <a href={`tel:${dossier.telefoon}`} className="flex items-center gap-2 text-sm text-gold hover:text-gold/80">
                    <Phone size={14} /> {dossier.telefoon}
                  </a>
                )}
                {dossier.email && (
                  <a href={`mailto:${dossier.email}`} className="flex items-center gap-2 text-sm text-gold hover:text-gold/80">
                    <Mail size={14} /> {dossier.email}
                  </a>
                )}
                {adressen.length > 0 && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{adressen[0].straat} {adressen[0].nummer}, {adressen[0].postcode} {adressen[0].stad}</span>
                  </div>
                )}
                {adressen.length === 0 && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{dossier.adres}, {dossier.postcode} {dossier.stad}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Financieel */}
            {(dossier.totaalBedrag > 0 || dossier.saldo > 0) && (
              <Card>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Financieel</h3>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Totaalbedrag</span>
                    <span className="text-sm font-semibold text-gray-900">{formatValuta(dossier.totaalBedrag)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Openstaand saldo</span>
                    <span className={clsx('text-sm font-semibold', Number(dossier.saldo) > 0 ? 'text-red-600' : 'text-green-600')}>
                      {formatValuta(dossier.saldo)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={clsx(
                      'text-xs font-medium px-2 py-0.5 rounded-full',
                      dossier.financieleStatus === 'groen' ? 'bg-green-100 text-green-700' :
                      dossier.financieleStatus === 'oranje' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700',
                    )}>
                      {dossier.financieleStatus === 'groen' ? 'In orde' :
                       dossier.financieleStatus === 'oranje' ? 'Aandacht' : 'Probleem'}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Contactpersonen */}
            <Card>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contactpersonen</h3>
              {contacten.length === 0 ? (
                <p className="text-sm text-gray-400">Geen contacten gekoppeld</p>
              ) : (
                <div className="space-y-3">
                  {contacten.map((dc: any) => (
                    <div key={dc.id} className="flex items-start gap-2">
                      <User size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{dc.contactpersoon.naam}</p>
                        {dc.rol && <p className="text-xs text-gray-500">{dc.rol}</p>}
                        {dc.contactpersoon.telefoon && (
                          <a href={`tel:${dc.contactpersoon.telefoon}`} className="text-xs text-gold hover:text-gold/80">
                            {dc.contactpersoon.telefoon}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Documenten */}
            <Card>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Documenten</h3>
              {documenten.length === 0 ? (
                <p className="text-sm text-gray-400">Geen documenten</p>
              ) : (
                <div className="space-y-2">
                  {documenten.slice(0, 5).map((doc: any) => (
                    <div key={doc.id} className="flex items-center gap-2">
                      <FileText size={14} className="text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-700 truncate">{doc.naam}</p>
                        <p className="text-[11px] text-gray-400">{formatDatum(doc.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                  {documenten.length > 5 && (
                    <p className="text-xs text-gold">+{documenten.length - 5} meer</p>
                  )}
                </div>
              )}
            </Card>

            {/* Communicatie */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Communicatie</h3>
                {commData && commData.totalUnread > 0 && (
                  <span className="bg-gold text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {commData.totalUnread > 9 ? '9+' : commData.totalUnread}
                  </span>
                )}
              </div>
              {!commData || commData.totalMessages === 0 ? (
                <p className="text-sm text-gray-400">Geen berichten</p>
              ) : (
                <div className="space-y-2">
                  {commData.recentMessages.slice(0, 4).map((msg) => (
                    <div key={msg.id} className="flex items-start gap-2">
                      <span className="text-xs mt-0.5">
                        {msg.channel === 'whatsapp' ? '📱' : msg.channel === 'email' ? '✉️' : msg.channel === 'sms' ? '📲' : '💬'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-700 truncate">{msg.content}</p>
                        <p className="text-[10px] text-gray-400">{msg.senderName} · {formatDatum(msg.createdAt)}</p>
                      </div>
                      {!msg.readAt && msg.senderType === 'customer' && (
                        <span className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => navigate('/communicatie')}
                    className="flex items-center gap-1.5 text-xs text-gold hover:text-gold/80 mt-1"
                  >
                    <MessageCircle size={12} /> Alle berichten bekijken
                  </button>
                </div>
              )}
            </Card>

            {/* Service Tickets */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Service</h3>
                {serviceTickets.filter((t: any) => !['OPGELOST', 'GESLOTEN'].includes(t.status)).length > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {serviceTickets.filter((t: any) => !['OPGELOST', 'GESLOTEN'].includes(t.status)).length} open
                  </span>
                )}
              </div>
              {serviceTickets.length === 0 ? (
                <p className="text-sm text-gray-400">Geen service tickets</p>
              ) : (
                <div className="space-y-2">
                  {serviceTickets.slice(0, 5).map((ticket: any) => (
                    <div
                      key={ticket.id}
                      onClick={() => navigate(`/service/ticket/${ticket.id}`)}
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-mono text-gold">{ticket.ticketNummer}</span>
                        <span className="text-xs text-gray-500 truncate">{ticket.type.replace('_', ' ').toLowerCase()}</span>
                      </div>
                      <span className={clsx('px-1.5 py-0.5 rounded text-[9px] font-bold text-white', {
                        'bg-blue-500': ticket.status === 'NIEUW',
                        'bg-amber-500': ticket.status === 'IN_BEHANDELING',
                        'bg-purple-500': ['WACHT_OP_ONDERDEEL', 'WACHT_OP_KLANT', 'INGEPLAND'].includes(ticket.status),
                        'bg-green-500': ticket.status === 'OPGELOST',
                        'bg-gray-500': ticket.status === 'GESLOTEN',
                      })}>
                        {ticket.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* Garantie info */}
              {garantieInfo.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase mb-1">Garantie</p>
                  {garantieInfo.map((g: any) => (
                    <p key={g.id} className="text-xs text-gray-600">
                      {g.type}: tot {formatDatum(g.eindDatum)}
                    </p>
                  ))}
                </div>
              )}
              <button
                onClick={() => navigate(`/service/nieuw?dossierId=${id}`)}
                className="flex items-center gap-1.5 text-xs text-gold hover:text-gold/80 mt-2"
              >
                <Wrench size={12} /> Nieuw service ticket
              </button>
            </Card>

            {/* Leverbonnen */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Leverbonnen</h3>
                {(leverbonnenData?.items || []).filter((l: any) => l.status === 'NIEUW').length > 0 && (
                  <span className="bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {(leverbonnenData?.items || []).filter((l: any) => l.status === 'NIEUW').length} nieuw
                  </span>
                )}
              </div>
              {!leverbonnenData?.items || leverbonnenData.items.length === 0 ? (
                <p className="text-sm text-gray-400">Geen leverbonnen</p>
              ) : (
                <div className="space-y-2">
                  {leverbonnenData.items.slice(0, 5).map((lb: any) => (
                    <div
                      key={lb.id}
                      onClick={() => navigate(`/logistiek/leverbonnen`)}
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded p-1 -m-1 transition"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Package size={12} className="text-gold shrink-0" />
                        <span className="text-xs text-gray-600 truncate">{lb.leverancier}</span>
                        {lb.referentie && <span className="text-[10px] text-gray-400 font-mono">{lb.referentie}</span>}
                      </div>
                      <span className={clsx('px-1.5 py-0.5 rounded text-[9px] font-bold text-white', {
                        'bg-blue-500': lb.status === 'NIEUW',
                        'bg-green-500': lb.status === 'VERWERKT',
                        'bg-red-500': lb.status === 'PROBLEEM',
                      })}>
                        {lb.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => navigate(`/logistiek/leverbonnen`)}
                className="flex items-center gap-1.5 text-xs text-gold hover:text-gold/80 mt-2"
              >
                <Package size={12} /> Alle leverbonnen
              </button>
            </Card>

            {/* Afspraken */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Afspraken</h3>
                <button
                  onClick={() => navigate('/planning')}
                  className="flex items-center gap-1 text-[11px] text-gold hover:text-gold/80"
                >
                  <CalendarPlus size={12} /> Inplannen
                </button>
              </div>
              {!afsprakenData || afsprakenData.afspraken.length === 0 ? (
                <p className="text-sm text-gray-400">Geen afspraken</p>
              ) : (
                <div className="space-y-2">
                  {afsprakenData.afspraken.slice(0, 5).map((a) => {
                    const dr = a.datums[0];
                    const isPast = a.bevestigdeDatum && new Date(a.bevestigdeDatum) < new Date();
                    const isBevestigd = a.status === 'bevestigd';
                    let templateNaam: string;
                    try { templateNaam = JSON.parse(a.trajectstap.template.naam).nl; } catch { templateNaam = a.trajectstap.template.naam; }
                    return (
                      <div key={a.id} className="flex items-start gap-2">
                        <span className="text-xs mt-0.5">
                          {isPast && isBevestigd ? '✅' : isBevestigd ? '📅' : '⏳'}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-700 truncate">{a.titel}</p>
                          <div className="flex items-center gap-2 text-[10px] text-gray-400">
                            <span>{a.trajectstap.template.icon} {templateNaam}</span>
                            {dr && <span>{dr.vanUur || ''}</span>}
                            {a.bevestigdeDatum && <span>{formatDatum(a.bevestigdeDatum)}</span>}
                          </div>
                        </div>
                        {a.herinneringVerstuurd && (
                          <span className="text-green-500 flex-shrink-0 mt-1" title="Herinnering verstuurd">🔔</span>
                        )}
                      </div>
                    );
                  })}
                  {afsprakenData.afspraken.length > 5 && (
                    <p className="text-xs text-gold">+{afsprakenData.afspraken.length - 5} meer</p>
                  )}
                </div>
              )}
            </Card>

            {/* Belangrijke Datums */}
            <Card>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Belangrijke datums</h3>
              {belangrijkeDatums.length === 0 ? (
                <p className="text-sm text-gray-400">Geen datums gepland</p>
              ) : (
                <div className="space-y-2.5">
                  {belangrijkeDatums.map((d: any, i: number) => (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{d.icon}</span>
                        <span className="text-sm text-gray-700">{d.naam}</span>
                      </div>
                      <span className={clsx(
                        'text-xs font-medium',
                        d.status === 'completed' ? 'text-green-600' : 'text-gray-500',
                      )}>
                        {formatDatum(d.datum)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}
